import { Typography, Form, FormInstance, Button, Alert, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { PredefinedReasonCategory } from '../../../shared/Constants';
import { useApproverRecommendationPage } from './useApproverRecommendationPage';
import { InterviewResult } from '../InterviewReportCard/InterviewResult';
import {
	Application,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import {
	canSubmitApproverFeedback,
	isProposerSkippedToEnd,
} from '../../../shared/Utils';
import { RecommendationComponent } from '../RecommendationComponent';
import { useAuth } from '../../../hooks/useAuth';
import { ExclamationCircleFilled } from '@ant-design/icons';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface ApproverRecommendationCollectionPageProps {
	form: FormInstance;
	application?: Application;
}

const SectionHeader = ({
	heading,
	description,
}: {
	heading: string;
	description?: string;
}) => {
	return (
		<div className="flex flex-col mb-[8px]">
			<Text className="font-sans font-bold text-lg text-primary-blue">
				{heading}
			</Text>
			{description && (
				<Text className="mb-[8px]" type="secondary">
					{description}
				</Text>
			)}
		</div>
	);
};

export const ApproverRecommendationCollectionPage = ({
	form,
	application,
}: ApproverRecommendationCollectionPageProps) => {
	const { t } = useTranslation();
	const { role } = useAuth();
	const isApprover = role === UserRole.APPROVER;

	const canDisplayComments =
		role === UserRole.APPROVER || role === UserRole.ADMIN;

	const isSkippedToEnd = isProposerSkippedToEnd(
		application?.proposerRecommendation
	);

	const approveRecommendation = application?.approverRecommendation;
	const isApproverRecommendationDone =
		approveRecommendation && Object.keys(approveRecommendation).length > 0;

	return (
		<div className="flex flex-col h-full min-h-0 max-w-[600px]">
			<Form
				name="basic"
				layout="vertical"
				size="middle"
				labelCol={{ span: 16 }}
				style={{ maxWidth: 600 }}
				initialValues={{ remember: true }}
				autoComplete="off"
				form={form}
				disabled={!canSubmitApproverFeedback(application) || !isApprover}
			>
				<SectionHeader heading={t('regionalCommitteeDecision')} />
				<InterviewResult
					application={application}
					finalRecommendationText="overallRecommendation"
				/>
				<div className="flex flex-row items-start">
					<div className="flex flex-col w-full">
						<SectionHeader
							heading={t('regionalCommitteeDecision')}
							description={t('provideRegionalCommitteeDecision') ?? ''}
						/>
						<RecommendationComponent
							commentsMandatory
							commentsLabel={'regionalCommitteeCommentsForAboveRecommendation'}
							commentsPlaceholder="commentsAreMandatory"
							form={form}
							category={
								isSkippedToEnd
									? PredefinedReasonCategory.SKIP
									: PredefinedReasonCategory.APPROVER_REJECT
							}
							displayComments={canDisplayComments}
							disableReview={!canDisplayComments}
							disabled={isApproverRecommendationDone || !isApprover}
						/>
					</div>
				</div>
			</Form>
		</div>
	);
};

export const ApproverRecommendationCollectionPageContainer = () => {
	const { t } = useTranslation();
	const {
		form,
		onSubmitClick,
		onCancelClick,
		application,
		submitButtonDisabled,
	} = useApproverRecommendationPage();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const { role } = useAuth();
	const isApprover = role === UserRole.APPROVER;
	const approveRecommendation = application?.approverRecommendation;
	const isApproverRecommendationDone =
		approveRecommendation && Object.keys(approveRecommendation).length > 0;

	return (
		<div className="flex justify-center bg-bground-blue">
			<div
				className={`flex flex-col h-full min-h-0 rounded px-5 py-4 ${
					isMobile ? 'w-full' : 'w-2/5 border border-solid border-[#E6E6E6]'
				}`}
			>
				<ApproverRecommendationCollectionPage
					form={form}
					application={application}
				/>
				<div className="flex flex-row p-[8px] justify-center">
					<Button onClick={onCancelClick}>{t('cancel')}</Button>
					<Button
						onClick={onSubmitClick}
						type="primary"
						disabled={
							isApproverRecommendationDone ||
							!isApprover ||
							submitButtonDisabled
						}
						className="ml-[16px]"
					>
						{t('submit')}
					</Button>
				</div>
				{!application?.isApprover && (
					<Alert
						message={
							<div className="text-center w-full">
								<ExclamationCircleFilled className="text-[#faad14]" />{' '}
								{t('approversAreAllowed')}
							</div>
						}
						type="warning"
					/>
				)}
			</div>
		</div>
	);
};
